import React, { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VideocamOff, OpenInFull, Close } from '@mui/icons-material';
import {
  setCounterOffer, setFeedback,
  approveSelection, rejectSelection, getHighlights,
  setDiamondStatus,
  setReviewStatus,
} from '../../redux/slices/selectionSlice';
import { setAlertText, setSeverity, setToast } from '../../redux/slices/commonComponentSlice';

// ── Past-comment helpers ──────────────────────────────────────
function parseFeedback(raw) {
  if (!raw) return [];
  return raw.split(/\n?---\n?/).map(block => {
    const lines       = block.trim().split('\n');
    const headerMatch = lines[0].match(/^\[(.+?)\s*\|\s*(.+?)\]$/);
    if (!headerMatch) return null;
    return {
      user:    headerMatch[1].trim(),
      date:    headerMatch[2].trim(),
      comment: lines.slice(1).join('\n').trim(),
    };
  }).filter(Boolean);
}

function timeAgo(isoDate) {
  const diff   = Date.now() - new Date(isoDate).getTime();
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(mins  / 60);
  const days   = Math.floor(hours / 24);
  const months = Math.floor(days  / 30);
  if (months >= 1) return months + 'mo ago';
  if (days   >= 1) return days   + 'd ago';
  if (hours  >= 1) return hours  + 'h ago';
  return Math.max(mins, 1) + 'm ago';
}

const PastComments = ({ raw }) => {
  const entries       = parseFeedback(raw);
  const [pos, setPos] = useState(null);
  if (!entries.length) return null;

  const handleEnter = (e) => {
    const rect        = e.currentTarget.getBoundingClientRect();
    const tooltipW    = 300;
    const iconCenterX = rect.left + rect.width / 2;
    setPos({
      bottom: window.innerHeight - rect.top + 6,
      left:   Math.max(8, iconCenterX - tooltipW / 2),
    });
  };

  return (
    <span
      className="sel-past-comments-wrap"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setPos(null)}
    >
      <span className="sel-past-icon">💬 {entries.length}</span>
      {pos && (
        <div className="sel-past-tooltip" style={{ position: 'fixed', bottom: pos.bottom, left: pos.left }}>
          {entries.map((e, i) => (
            <div key={i} className="sel-past-entry">
              <div className="sel-past-meta">
                <strong>{e.user}</strong>
                <span className="sel-past-time">{timeAgo(e.date)}</span>
              </div>
              <div className="sel-past-comment">
                {e.comment}
              </div>
            </div>
          ))}
        </div>
      )}
    </span>
  );
};

// ── Offer calc ────────────────────────────────────────────────
function calcOffer(type, value, diamond) {
  const listPrice = parseFloat(diamond['Rap $/Ct'] || 0) ||
    (() => {
      const ppc    = parseFloat(diamond['$/Ct'] || 0);
      const rapPct = parseFloat(diamond['Rap %'] || 0);
      return rapPct !== -100 ? ppc / (1 + rapPct / 100) : 0;
    })();
  const carats = parseFloat(diamond.Carats || 0);
  let offerRap, offerPPC, offerTotal;
  if (type === 'rap') {
    offerRap   = parseFloat(value);
    offerPPC   = listPrice * (1 + offerRap / 100);
    offerTotal = offerPPC * carats;
  } else if (type === 'ppc') {
    offerPPC   = parseFloat(value);
    offerTotal = offerPPC * carats;
    offerRap   = listPrice > 0 ? ((offerPPC / listPrice) - 1) * 100 : 0;
  } else {
    offerTotal = parseFloat(value);
    offerPPC   = carats > 0 ? offerTotal / carats : 0;
    offerRap   = listPrice > 0 ? ((offerPPC / listPrice) - 1) * 100 : 0;
  }
  return {
    offerRap:   offerRap.toFixed(2),
    offerPPC:   offerPPC.toFixed(2),
    offerTotal: offerTotal.toFixed(2),
  };
}

const fmt = (n, decimals = 0) => {
  const v = parseFloat(n);
  return isNaN(v) ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// ── DiamondCard ───────────────────────────────────────────────
const DiamondCard = ({ diamond, accountDetails }) => {
  const dispatch      = useDispatch();
  const ldId          = String(diamond['LD ID']);
  const counterOffer  = useSelector(s => s.selection.counterOffers[ldId]) || {};
  const feedbackVal   = useSelector(s => s.selection.feedback[ldId])      || '';
  // const currentStatus = diamond.Status || '';
  const currentStatus = useSelector(s => s.selection.reviewStatuses?.[ldId]) || '';
  const highlights = getHighlights(diamond);
  const videoUrls  = (diamond.video || '').split(',').map(s => s.trim()).filter(Boolean);
  const [slide, setSlide]               = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [modalOpen, setModalOpen]         = useState(false);
  const iframeRef = useRef(null);

  const handleOfferBlur = useCallback((e, type) => {
    if (!e.target.value) return;
    dispatch(setCounterOffer({ ldId, ...calcOffer(type, e.target.value, diamond) }));
  }, [diamond, dispatch, ldId]);

  const handleFeedback = useCallback(e => {
    dispatch(setFeedback({ ldId, value: e.target.value }));
  }, [dispatch, ldId]);

  // const handleStatus = useCallback(async (newStatus) => {
  //   const actionKey = newStatus === 'Shortlisted' ? 'approve' : 'reject';
  //   setActionLoading(actionKey);

  //   const payload = {
  //     ldId,
  //     certNo:  diamond['Cert No'] || '',
  //     user:    accountDetails?.name || accountDetails?.account_name || accountDetails?.email || '',
  //     diamond,
  //     offerRap:   counterOffer.offerRap   || '',
  //     offerPPC:   counterOffer.offerPPC   || '',
  //     offerTotal: counterOffer.offerTotal || '',
  //     feedback:   feedbackVal,
  //   };

  //   try {
  //     if (newStatus === 'Shortlisted') {
  //       await dispatch(approveSelection(payload)).unwrap();
  //       dispatch(setAlertText('Approved and added to Selection sheet'));
  //     } else {
  //       await dispatch(rejectSelection(payload)).unwrap();
  //       dispatch(setAlertText('Rejected — removed from Selection sheet'));
  //     }
  //     dispatch(setSeverity('success'));
  //     dispatch(setToast(true));
  //   } catch (err) {
  //     dispatch(setAlertText('Action failed: ' + err));
  //     dispatch(setSeverity('error'));
  //     dispatch(setToast(true));
  //   } finally {
  //     setActionLoading(null);
  //   }
  // }, [dispatch, ldId, diamond, accountDetails, counterOffer, feedbackVal]);

  // ── Replace handleStatus entirely ──

  const handleStatus = useCallback(async (newStatus) => {
    const actionKey = newStatus === 'Shortlisted' ? 'approve' : 'reject';
    setActionLoading(actionKey);
    try {
      dispatch(setReviewStatus({ ldId, status: newStatus }));
      dispatch(setAlertText(newStatus === 'Shortlisted' ? 'Approved' : 'Rejected'));
      dispatch(setSeverity('success'));
      dispatch(setToast(true));
    } catch (err) {
      dispatch(setAlertText('Action failed: ' + err));
      dispatch(setSeverity('error'));
      dispatch(setToast(true));
    } finally {
      setActionLoading(null);
    }
  }, [dispatch, ldId]);

  const measureDisplay = diamond.Measurements
    ? diamond.Ratio ? diamond.Measurements + ' (' + diamond.Ratio + ')' : diamond.Measurements
    : '';

  return (
    <div className={`sel-diamond-card col-sm-12 col-md-6 col-xl-4${
        currentStatus === 'Shortlisted' ? ' card-approved' : 
        currentStatus === 'Rejected'    ? ' card-rejected'  : ''
      }`}>
      <div className="sel-card">

        {/* ── Video carousel ── */}
        <div className="sel-carousel">
          {videoUrls.length > 0 ? (
            <>
              <div className="sel-carousel-slide">
                <iframe ref={iframeRef} src={videoUrls[slide]} frameBorder="0" loading="lazy"
                  title={'Diamond ' + ldId} allowFullScreen />
              </div>
              {videoUrls.length > 1 && (
                <>
                  <button className="sel-carousel-btn prev" onClick={() => setSlide(i => Math.max(0, i - 1))} disabled={slide === 0}>‹</button>
                  <button className="sel-carousel-btn next" onClick={() => setSlide(i => Math.min(videoUrls.length - 1, i + 1))} disabled={slide === videoUrls.length - 1}>›</button>
                  <div className="sel-carousel-dots">
                    {videoUrls.map((_, i) => (
                      <div key={i} className={'sel-dot' + (slide === i ? ' active' : '')} onClick={() => setSlide(i)} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="sel-no-video">
              <VideocamOff style={{ fontSize: 36, color: '#ccc' }} />
              <span style={{ fontSize: 11, color: '#bbb' }}>No video</span>
            </div>
          )}
        </div>

        <div className="sel-card-body">

          {/* ── ID row ── */}
          <div className="sel-card-id-row">
            <span className="sel-card-id">ID: {diamond['LD ID']}</span>
            {videoUrls.length > 0 && (
              <button
                className="sel-card-expand-btn"
                onClick={() => setModalOpen(true)}
                title="Open video fullscreen"
              >
                <OpenInFull style={{ fontSize: 15 }} />
              </button>
            )}
          </div>

          {/* ── Video modal ── */}
          {modalOpen && (
            <div className="sel-video-modal-backdrop" onClick={() => setModalOpen(false)}>
              <div className="sel-video-modal" onClick={e => e.stopPropagation()}>
                <button className="sel-video-modal-close" onClick={() => setModalOpen(false)}>
                  <Close style={{ fontSize: 20 }} />
                </button>
                <iframe
                  src={videoUrls[slide]}
                  frameBorder="0"
                  allowFullScreen
                  title={'Diamond ' + ldId + ' fullscreen'}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </div>
            </div>
          )}

          {/* ── Meta badges ── */}
          <div className="sel-card-meta-row">
            {diamond.bucket       && <span className="sel-bucket-badge">{diamond.bucket}</span>}
            {diamond.Lab          && <span className="sel-lab-badge">{diamond.Lab.toLowerCase()}</span>}
            {diamond['Stock No']  && <span className="sel-meta-badge">Stock: {diamond['Stock No']}</span>}
            {diamond['Cert No']   && <span className="sel-meta-badge">Cert: {diamond['Cert No']}</span>}
            {diamond.Location     && <span className="sel-meta-badge">📍 {diamond.Location}</span>}
            {diamond['Demand Name'] && (
              <span className="sel-demand-badge">🎯 {diamond['Demand Name']}</span>
            )}
          </div>

          {/* ── Approve / Reject ── */}
          {/* <div className="sel-status-btns">
            <button
              className={'sel-status-btn shortlist' + (currentStatus === 'Shortlisted' ? ' active' : '')}
              onClick={() => handleStatus('Shortlisted')}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'approve' ? '…' : 'Approve'}
            </button>
            <button
              className={'sel-status-btn reject' + (currentStatus === 'Rejected' ? ' active' : '')}
              onClick={() => handleStatus('Rejected')}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'reject' ? '…' : 'Reject'}
            </button>
          </div> */}
          
          <div className="sel-status-btns">
            <button
              className={'sel-status-btn shortlist' + (currentStatus === 'Shortlisted' ? ' active' : '')}
              onClick={() => handleStatus('Shortlisted')}
              disabled={actionLoading !== null || currentStatus === 'Shortlisted'}
            >
              {actionLoading === 'approve' ? 'Approving…' : currentStatus === 'Shortlisted' ? 'Approved' : 'Approve'}
            </button>
            <button
              className={'sel-status-btn reject' + (currentStatus === 'Rejected' ? ' active' : '')}
              onClick={() => handleStatus('Rejected')}
              disabled={actionLoading !== null || currentStatus === 'Rejected'}
            >
              {actionLoading === 'reject' ? 'Rejecting…' : currentStatus === 'Rejected' ? 'Rejected' : 'Reject'}
            </button>
          </div>

          {/* ── 4Cs ── */}
          <div className="sel-4cs-grid">
            <div className="sel-4cs-col">
              <span className="sel-4cs-val">{diamond.Shape}</span>
              <span className="sel-4cs-sub">{diamond.Cut || ' '}</span>
            </div>
            <div className="sel-4cs-col">
              <span className="sel-4cs-val">{diamond.Carats}</span>
              <span className="sel-4cs-sub">&nbsp;</span>
            </div>
            <div className="sel-4cs-col">
              <span className="sel-4cs-val">{diamond.Color}</span>
              <span className="sel-4cs-sub">{diamond.Polish || ' '}</span>
            </div>
            <div className="sel-4cs-col">
              <span className="sel-4cs-val">{diamond.Clarity}</span>
              <span className="sel-4cs-sub">{diamond.Sym || ' '}</span>
            </div>
            <div className="sel-4cs-col">
              <span className="sel-4cs-val">{diamond['Rap %']}%</span>
              <span className="sel-4cs-sub">{diamond.Fluor || ' '}</span>
            </div>
          </div>

          {/* ── Compact stats ── */}
          <div className="sel-stats-compact">
            <span><strong>Need(2X)</strong>: {diamond.need_2x ?? '—'}/{diamond.grp_need_2x ?? '—'}</span>
            <span><strong>Inv</strong>: {diamond.inv ?? '—'}/{diamond.grp_inv ?? '—'}</span>
            <span><strong>Sales(90D)</strong>: {diamond.sales_90d ?? '—'}/{diamond.grp_sales_90d ?? '—'}</span>
            <span><strong>Sales(YTD)</strong>: {diamond.sales_ytd ?? '—'}/{diamond.grp_sales_ytd ?? '—'}</span>
            <span><strong>Sales(1Y)</strong>: {diamond.sales_1y ?? '—'}/{diamond.grp_sales_1y ?? '—'}</span>
          </div>

          {/* ── Prices ── */}
          <div className="sel-card-prices">
            <span><strong>Rap</strong>: {diamond['Rap %']}%</span>
            <span><strong>PPC</strong>: ${fmt(diamond['$/Ct'])}</span>
            <span><strong>Total</strong>: ${fmt(diamond['Total Price'])}</span>
          </div>

          {/* ── Tags ── */}
          {highlights.length > 0 && (
            <div className="sel-tags">
              {highlights.map((h, i) => (
                <span key={i} className={'sel-tag ' + h.type}>{h.text}</span>
              ))}
            </div>
          )}

          {/* ── Counter-offer inputs ── */}
          <div className="sel-offer-inputs">
            <input
              type="number" placeholder="Offer Rap%"
              defaultValue={counterOffer.offerRap || ''}
              key={'rap-' + ldId + '-' + counterOffer.offerRap}
              onBlur={e => handleOfferBlur(e, 'rap')}
            />
            <input
              type="number" placeholder="Offer PPC"
              defaultValue={counterOffer.offerPPC || ''}
              key={'ppc-' + ldId + '-' + counterOffer.offerPPC}
              onBlur={e => handleOfferBlur(e, 'ppc')}
            />
            <input
              type="number" placeholder="Offer Total"
              defaultValue={counterOffer.offerTotal || ''}
              key={'total-' + ldId + '-' + counterOffer.offerTotal}
              onBlur={e => handleOfferBlur(e, 'total')}
            />
          </div>

          {/* ── Feedback ── */}
          <div className="sel-feedback-row">
            <textarea
              className="sel-feedback-textarea"
              placeholder="Add comment…"
              value={feedbackVal}
              onChange={handleFeedback}
            />
          </div>

          {/* ── User + status + past comments ── */}
          {diamond.User && (
            <div className="sel-user-row">
              👤 {diamond.User}{currentStatus ? ': ' + currentStatus : ''}
              <PastComments raw={diamond.Feedback} />
            </div>
          )}

          {/* ── Measurements ── */}
          {(measureDisplay || diamond['Table %'] != null || diamond['Depth %'] != null) && (
            <div className="sel-card-measure-row">
              {measureDisplay && <span>{measureDisplay}</span>}
              {diamond['Table %'] != null && <span>T%: {diamond['Table %']}</span>}
              {diamond['Depth %'] != null && <span>D%: {diamond['Depth %']}</span>}
            </div>
          )}

          {/* ── KTS ── */}
          {diamond.KTS && (
            <div className="sel-card-kts">
              <strong>KTS</strong>: {diamond.KTS}
            </div>
          )}

          {/* ── Cert comment ── */}
          <div className="sel-card-cert">
            <strong>Cert</strong>: {diamond['Cert Comment'] || ''}
          </div>

          {/* ── Shade / Milky ── */}
          <div className="sel-card-shade-row">
            <span>Shade: {diamond.Shade || 'none'}</span>
            <span>Milky: {diamond.Milky || 'none'}</span>
          </div>

          {/* ── Source CSV ── */}
          {diamond['Source CSV'] && (
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, textAlign: 'right' }}>
              {String(diamond['Source CSV']).replace(/^GN_RECO_/i, '')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiamondCard;
